import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_BASE_URL } from '../config';


// Async thunk for fetching departments
export const fetchDepartments = createAsyncThunk(
  'department/fetchDepartments',
  async (adminId, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/departments?adminId=${adminId}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch departments');
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch departments');
    }
  }
);

// Async thunk for creating a department
export const createDepartment = createAsyncThunk(
  'department/createDepartment',
  async ({ adminId, name, description }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/departments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminId, name, description }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create department');
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create department');
    }
  }
);

// Async thunk for updating a department
export const updateDepartment = createAsyncThunk(
  'department/updateDepartment',
  async ({ id, name, description }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/departments/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, description }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update department');
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update department');
    }
  }
);

// Async thunk for deleting a department
export const deleteDepartment = createAsyncThunk(
  'department/deleteDepartment',
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/departments/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete department');
      }
      return id;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete department');
    }
  }
);

const initialState = {
  departments: [],
  loading: false,
  error: null,
};

const departmentSlice = createSlice({
  name: 'department',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch departments
      .addCase(fetchDepartments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDepartments.fulfilled, (state, action) => {
        state.loading = false;
        state.departments = action.payload;
      })
      .addCase(fetchDepartments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create department
      .addCase(createDepartment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDepartment.fulfilled, (state, action) => {
        state.loading = false;
        state.departments.unshift(action.payload);
      })
      .addCase(createDepartment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update department
      .addCase(updateDepartment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDepartment.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.departments.findIndex((dep) => dep._id === action.payload._id);
        if (index !== -1) {
          state.departments[index] = action.payload;
        }
      })
      .addCase(updateDepartment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete department
      .addCase(deleteDepartment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDepartment.fulfilled, (state, action) => {
        state.loading = false;
        state.departments = state.departments.filter((dep) => dep._id !== action.payload);
      })
      .addCase(deleteDepartment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError } = departmentSlice.actions;
export default departmentSlice.reducer;
